import type { ComponentPropsWithRef } from 'react';

import { cn } from '@/lib/cn';

/**
 * Dense enterprise table primitives.
 *
 * Zebra-striped rows, a grey gradient header and visible cell borders - a grid
 * meant to be scanned, not a list of cards. These are presentational only:
 * sorting, selection and pagination belong to the feature that renders the
 * table, so it can keep that state in the URL or in a query key.
 */

export function TableContainer({ className, children, ...props }: ComponentPropsWithRef<'div'>) {
  return (
    /**
     * `relative` is load-bearing, not decoration.
     *
     * A static container does not become the containing block for absolutely
     * positioned descendants, so they are measured against the page instead
     * and escape this scroller entirely. The `sr-only` heading on an actions
     * column is exactly that: it sits at the far right of a table wider than
     * a phone, and without this the whole page - navigation included - slid
     * sideways when the table did.
     */
    <div className={cn('relative w-full overflow-x-auto', className)} {...props}>
      {children}
    </div>
  );
}

export function Table({ className, children, ...props }: ComponentPropsWithRef<'table'>) {
  return (
    <table className={cn('w-full border-collapse text-left text-xs', className)} {...props}>
      {children}
    </table>
  );
}

export function THead({ className, children, ...props }: ComponentPropsWithRef<'thead'>) {
  return (
    <thead className={cn('heading-bar border-b', className)} {...props}>
      {children}
    </thead>
  );
}

export function TBody({ className, children, ...props }: ComponentPropsWithRef<'tbody'>) {
  return (
    /* `chrome-striped` provides the alternating row shading and the hover
       highlight; see styles/index.css. */
    <tbody className={cn('chrome-striped divide-y divide-border', className)} {...props}>
      {children}
    </tbody>
  );
}

export interface TrProps extends ComponentPropsWithRef<'tr'> {
  /** Adds a pointer cursor for row-click navigation. */
  interactive?: boolean;
}

export function Tr({ interactive = false, className, children, ...props }: TrProps) {
  return (
    <tr className={cn('transition-colors', interactive && 'cursor-pointer', className)} {...props}>
      {children}
    </tr>
  );
}

/**
 * Columns can opt out of narrow screens. A phone cannot show eight columns
 * legibly, and sideways scrolling to reach the status of a row is worse than
 * not showing the secondary ones at all.
 */
export type HideBelow = 'sm' | 'md' | 'lg';

const HIDE_BELOW: Record<HideBelow, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

export interface ThProps extends ComponentPropsWithRef<'th'> {
  align?: 'left' | 'center' | 'right';
  hideBelow?: HideBelow;
}

export function Th({ align = 'left', hideBelow, className, children, ...props }: ThProps) {
  return (
    <th
      scope="col"
      className={cn(
        'border-r border-border px-2.5 py-1.5 text-2xs font-bold text-content last:border-r-0',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        hideBelow && HIDE_BELOW[hideBelow],
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export interface TdProps extends ComponentPropsWithRef<'td'> {
  align?: 'left' | 'center' | 'right';
  hideBelow?: HideBelow;
}

export function Td({ align = 'left', hideBelow, className, children, ...props }: TdProps) {
  return (
    <td
      className={cn(
        /* Roomier rows on touch screens, dense on the desktop grid. */
        'border-r border-border px-2.5 py-2 text-content last:border-r-0 sm:py-1.5',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        hideBelow && HIDE_BELOW[hideBelow],
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}
